import ApiService from '../apiService';
import { trackEvent } from '@/utils/tracking';

const useV2DashboardsApi = false;

// helper function to get the appropriate API instance
const api = () => {
  return useV2DashboardsApi ? ApiService.instanceApiGateway : ApiService.instance;
};

export default {
  async fetchAll({ selectedAccountIds = [], limit = 100, offset = 0, search, sortBy, sortDirection }) {
    const query = new URLSearchParams();

    query.append('limit', limit);
    query.append('offset', offset);
    query.append('search', search || '');

    if (sortBy) {
      query.append('sortBy', sortBy);
      query.append('sortDirection', sortDirection === 'desc' ? 'desc' : 'asc');
    }

    if (selectedAccountIds.length > 0) {
      selectedAccountIds.forEach((accountId) => {
        query.append('accountIds', accountId);
      });
    }
    const response = await api().get(`/dashboards?${query}`, { timeout: 25000 });
    return response.data;
  },

  async fetchOneById(id) {
    const response = await api().get(`/dashboards/${id}`);
    return response.data;
  },

  async createOne(data) {
    const response = await api().post('/dashboards', data);

    trackEvent('dashboards', 'createOne');

    return response.data;
  },

  async deleteOneById(id) {
    await api().delete(`/dashboards/${id}`);

    trackEvent('dashboards', 'deleteOneById');
  },

  async addWidget(dashboardId, widgetData) {
    const payload = useV2DashboardsApi
      ? {
          dashboardId,
          widget: widgetData,
        }
      : widgetData;
    const response = await api().post(`/dashboards/${dashboardId}/widgets`, payload);

    trackEvent('dashboards', 'addWidget');

    return response.data;
  },

  async updateWidgetConfig(dashboardId, widgetId, config) {
    const response = await ApiService.instance.post(`/dashboards/${dashboardId}/widgets/${widgetId}/config`, config);
    // const response = await api().post(`/dashboards/${dashboardId}/widgets/${widgetId}/config`, config);

    trackEvent('dashboards', 'updateWidgetConfig');

    return response.data;
  },

  async updateWidgetOrder(dashboardId, order) {
    const response = await ApiService.instance.post(`/dashboards/${dashboardId}/widgets/order`, {
      // const response = await api().post(`/dashboards/${dashboardId}/widgets/order`, {
      order,
    });

    trackEvent('dashboards', 'updateWidgetOrder');

    return response.data;
  },

  async removeWidget(dashboardId, widgetId) {
    await api().delete(`/dashboards/${dashboardId}/widgets/${widgetId}`);

    trackEvent('dashboards', 'removeWidget');
  },

  async updateDashboardConfig(dashboardId, config) {
    const response = await api().post(`/dashboards/${dashboardId}/config`, config);
    trackEvent('dashboards', 'updateDashboardConfig');

    return response.data;
  },

  async fetchDataForWidget(dashboardId, widgetIdOrWidgetDefinition, dateRange, customParameters) {
    let response;
    if (typeof widgetIdOrWidgetDefinition === 'string') {
      const widgetId = widgetIdOrWidgetDefinition;
      const query = new URLSearchParams();
      query.append('dateRangeStart', dateRange.start.toISOString());
      query.append('dateRangeEnd', dateRange.end.toISOString());
      Object.entries(customParameters).forEach(([key, value]) => {
        query.append(`parameter_${key}`, value);
      });

      response = await ApiService.instance.get(`/dashboards/${dashboardId}/widgets/${widgetId}/data?${query}`);
      // response = await api().get(`/dashboards/${dashboardId}/widgets/${widgetId}/data?${query}`);
    } else {
      const widgetDefinition = widgetIdOrWidgetDefinition;
      const optionsToRemove = new Set(['chartOptions', 'twinMapOptions', 'twinTableOptions']);

      switch (widgetDefinition.type) {
        case 'twin-table':
          optionsToRemove.delete('twinTableOptions');
          break;

        case 'twin-map':
          optionsToRemove.delete('twinMapOptions');
          break;

        case 'chart':
          optionsToRemove.delete('chartOptions');
          break;
      }

      optionsToRemove.forEach((option) => {
        delete widgetDefinition[option];
      });
      response = await api().post(`/dashboards/${dashboardId}/widgets/__by_definition__/data`, {
        dashboardId,
        widgetDefinition,
        dateRange: {
          ...dateRange,
          start: dateRange.start.toISOString(),
          end: dateRange.end.toISOString(),
        },
        customParameters,
      });
    }
    return response.data;
  },

  async fetchDataForAllWidgetsOfDashboard(dashboardId, dateRange, customParameters) {
    return this.fetchDataForWidget(dashboardId, '__all__', dateRange, customParameters);
  },

  // Backend-driven, paginated + searchable data source for a single table widget (RD-9213).
  // Only the requested page of rows is loaded, so it scales to large twin/virtual-device sets.
  async fetchTwinTableData(
    dashboardId,
    widgetDefinition,
    { dateRange, customParameters, search, start, limit, orderBy }
  ) {
    const response = await api().post(`/dashboards/${dashboardId}/widgets/twin-table-data`, {
      widgetIdOrDefinition: widgetDefinition,
      dateRange: {
        ...dateRange,
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      },
      customParameters: customParameters || {},
      search: search || '',
      start: start || 0,
      limit: limit || 50,
      orderBy: orderBy || undefined,
    });
    return response.data;
  },

  async fetchAvailableStatesForDashboardFilter(dashboardId, twinFilter, virtualDeviceFilter, customParameters) {
    const response = await api().post('/dashboards/states', {
      dashboardId,
      twinFilter,
      virtualDeviceFilter,
      customParameters,
    });
    return response.data.states;
  },
};
